import * as SelectPrimitive from "@radix-ui/react-select";
import { ComponentProps, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/shared/components/ui/input-group";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Spinner } from "@/shared/components/ui/spinner";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useNumbersDocumentNumberListQuery,
  useNumbersDocumentNumberShowQuery,
} from "@/services/backend/numbers/api/document-number-api";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";

export function DocumentNumberSelect({
  value,
  documentKind = { active: false },
  serviceKind = { active: false },
  ...props
}: ComponentProps<typeof SelectPrimitive.Root> & {
  documentKind?: Filter<GSMDocumentKind>;
  serviceKind?: Filter<GSMServiceKind>;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading, error, isFetching } =
    useNumbersDocumentNumberListQuery(
      {
        string: {
          active: true,
          mode: FilterMode.Like,
          values: [search],
        },
        documentKind,
        serviceKind,
      },
      { skip: !search || search.length < 2 },
    );

  const doSearch = () => {
    setSearch(searchInput);
    setSearchInput("");
  };

  const { data: selectedNumber } = useNumbersDocumentNumberShowQuery(
    { documentId: value ?? NullUUID },
    { skip: !value || value === "" },
  );

  const numbers = useMemo(() => {
    if (isLoading || isFetching) {
      return [];
    }
    const list = data?.numbers ?? [];
    if (!selectedNumber) {
      return list;
    }
    return list.filter((n) => n.documentId !== selectedNumber.documentId);
  }, [data?.numbers, isFetching, isLoading, selectedNumber]);

  return (
    <Select value={value} {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Dokument auswählen" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup className="mb-2 flex gap-2">
          <InputGroup
            className="border-0 shadow-none"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                doSearch();
              }
            }}
          >
            <InputGroupInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value ?? "")}
              placeholder={t("Suche nach Dokument...")}
            />
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
          </InputGroup>
          <Button onClick={doSearch} variant="default">
            <Search />
          </Button>
        </SelectGroup>
        {selectedNumber && (
          <SelectGroup>
            <SelectLabel>{t("Ausgewählt:")}</SelectLabel>
            <SelectItem value={selectedNumber.documentId}>
              {selectedNumber.string}
            </SelectItem>
          </SelectGroup>
        )}
        <SelectGroup>
          <SelectLabel>{t("Suchergebnisse:")}</SelectLabel>
          {(isLoading || isFetching) && (
            <SelectItem value="loading" disabled>
              <Spinner />
              <span>{t("Suche")}</span>
            </SelectItem>
          )}
          {error && (
            <SelectItem value="error" disabled>
              <RTKQueryErrorAlert error={error} />
            </SelectItem>
          )}
          {searchInput.length > 0 && data && numbers.length === 0 && (
            <SelectItem value={NullUUID} disabled>
              {t("Keine Dokumente gefunden")}
            </SelectItem>
          )}
          {numbers.map((number) => (
            <SelectItem value={number.documentId} key={number.documentId}>
              {number.string}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
